import * as React from 'react';
import { GuestVoucherCard, type GuestVoucherDto } from './GuestVoucherCard';
import { VoucherActionModal } from './VoucherActionModal';
import { PrinterPickerModal } from './PrinterPickerModal';
import { RevertModal } from './RevertModal';
import { AdHocVoucherModal } from './AdHocVoucherForm';
import { OverrideBadge, OverrideModal } from './OverrideEditor';
import { VoucherToggleConfirmModal } from './VoucherToggleConfirmModal';
import './GuestVouchers.css';

interface PersonVoucherGroup {
    personId: string;
    personName: string | null;
    vouchers: GuestVoucherDto[];
}

interface VouchersForReservationResponse {
    reservationId: string;
    people: PersonVoucherGroup[];
    companyManagerBaseUrl: string | null;
    vouchersEnabled: boolean;
}

export interface GuestVoucherOverrideDto {
    guestVoucherOverrideId: string;
    reservationId: string;
    personId: string;
    personName: string | null;
    redPerWeek: number;
    bluePerWeek: number;
    notes: string | null;
}

interface DateVoucherGroup {
    label: string;
    sortKey: string;
    vouchers: GuestVoucherDto[];
}

function formatGroupDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
}

function groupVouchersByDate(vouchers: GuestVoucherDto[]): DateVoucherGroup[] {
    const generated = vouchers.filter(v => v.source !== 'Manual');
    const adhoc = vouchers.filter(v => v.source === 'Manual');

    const dateMap = new Map<string, GuestVoucherDto[]>();
    for (const v of generated) {
        const key = v.windowStart.slice(0, 10);
        if (!dateMap.has(key)) dateMap.set(key, []);
        dateMap.get(key)!.push(v);
    }

    const groups: DateVoucherGroup[] = Array.from(dateMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateKey, vs]) => ({
            label: formatGroupDate(dateKey),
            sortKey: dateKey,
            vouchers: vs,
        }));

    if (adhoc.length > 0) {
        groups.push({ label: 'Ad-hoc', sortKey: 'zzz', vouchers: adhoc });
    }

    return groups;
}

interface Props {
    reservationId: string;
    onClose: () => void;
}

export const GuestVouchersModal: React.FC<Props> = ({ reservationId, onClose }) => {
    const [data, setData] = React.useState<VouchersForReservationResponse | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [overrides, setOverrides] = React.useState<GuestVoucherOverrideDto[]>([]);
    const [showDeleted, setShowDeleted] = React.useState<Record<string, boolean>>({});

    // Toggle state
    const [isToggling, setIsToggling] = React.useState(false);
    const [toggleConfirm, setToggleConfirm] = React.useState<'enable' | 'disable' | null>(null);

    // Sub-modal states
    const [convertingVoucher, setConvertingVoucher] = React.useState<GuestVoucherDto | null>(null);
    const [printingVoucher, setPrintingVoucher] = React.useState<GuestVoucherDto | null>(null);
    const [showPrintAllPicker, setShowPrintAllPicker] = React.useState(false);
    const [revertingVoucher, setRevertingVoucher] = React.useState<GuestVoucherDto | null>(null);
    const [adhocPersonId, setAdhocPersonId] = React.useState<string | null>(null);
    const [overridePersonId, setOverridePersonId] = React.useState<string | null>(null);

    const vouchersEnabled = data?.vouchersEnabled ?? false;

    const loadVouchers = React.useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`/api/sales/guest-vouchers/${reservationId}`);
            if (!response.ok) throw new Error('Failed to load vouchers');
            setData(await response.json());
        } catch (e: any) {
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    }, [reservationId]);

    const loadOverrides = React.useCallback(async () => {
        try {
            const response = await fetch(`/api/sales/guest-vouchers/overrides/${reservationId}`);
            if (response.ok) setOverrides(await response.json());
        } catch { /* ignore */ }
    }, [reservationId]);

    React.useEffect(() => { loadVouchers(); loadOverrides(); }, [loadVouchers, loadOverrides]);

    // Toggle handler
    const handleToggleConfirm = async () => {
        const action = toggleConfirm;
        if (!action) return;
        setToggleConfirm(null);
        setIsToggling(true);
        try {
            const endpoint = action === 'enable' ? 'enable-vouchers' : 'disable-vouchers';
            const response = await fetch(`/api/sales/guest-vouchers/${reservationId}/${endpoint}`, { method: 'POST' });
            if (!response.ok) {
                const result = await response.json().catch(() => ({}));
                alert(`Failed: ${result.error || `HTTP ${response.status}`}`);
            } else {
                await loadVouchers();
                loadOverrides();
            }
        } catch (e: any) {
            alert(`Failed: ${e.message || 'Network error'}`);
        } finally {
            setIsToggling(false);
        }
    };

    // Print handlers
    const handleThermalPrint = async (voucherId: string, printerId: string) => {
        await fetch(`/api/sales/guest-vouchers/${voucherId}/thermal-print?printerId=${printerId}`, { method: 'POST' });
        loadVouchers();
    };
    const handlePreviewPrint = async (voucherId: string) => {
        const response = await fetch(`/api/sales/guest-vouchers/${voucherId}/print`, { method: 'POST' });
        if (response.ok) { const r = await response.json(); if (r.previewUrl) window.open(r.previewUrl, '_blank'); }
        loadVouchers();
    };
    const handleThermalPrintAll = async (printerId: string) => {
        await fetch(`/api/sales/guest-vouchers/thermal-print-all/${reservationId}?printerId=${printerId}`, { method: 'POST' });
        loadVouchers();
    };
    const handlePreviewPrintAll = async () => {
        const response = await fetch(`/api/sales/guest-vouchers/print-all/${reservationId}`, { method: 'POST' });
        if (response.ok) { const r = await response.json(); r.previewUrls?.forEach((u: string) => window.open(u, '_blank')); }
        loadVouchers();
    };

    // Voucher actions
    const handleResolveFlag = async (voucher: GuestVoucherDto) => {
        if (!window.confirm(`Re-activate voucher V-${voucher.voucherNumber}?\n\n"${voucher.flagReason}"`)) return;
        await fetch(`/api/sales/guest-vouchers/${voucher.guestVoucherId}/resolve-flag`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ resolution: 'activate' }),
        });
        loadVouchers();
    };
    const handleVoid = async (voucher: GuestVoucherDto) => {
        if (!window.confirm(`Void voucher V-${voucher.voucherNumber}?`)) return;
        const r = await fetch(`/api/sales/guest-vouchers/${voucher.guestVoucherId}/void`, { method: 'POST' });
        if (!r.ok) { const j = await r.json(); alert(`Failed: ${j.error || 'Unknown error'}`); } else loadVouchers();
    };
    const handleUnvoid = async (voucher: GuestVoucherDto) => {
        if (!window.confirm(`Restore voucher V-${voucher.voucherNumber} to Active?`)) return;
        const r = await fetch(`/api/sales/guest-vouchers/${voucher.guestVoucherId}/unvoid`, { method: 'POST' });
        if (!r.ok) { const j = await r.json(); alert(`Failed: ${j.error || 'Unknown error'}`); } else loadVouchers();
    };

    const allVouchers = data?.people.flatMap(p => p.vouchers) ?? [];
    const totalVouchers = allVouchers.filter(v => !v.deletedUtc).length;
    const totalActive = allVouchers.filter(v => v.status === 'Active' && !v.deletedUtc).length;
    const totalUsed = allVouchers.filter(v => v.status === 'Used' && !v.deletedUtc).length;
    const totalVoided = allVouchers.filter(v => v.status === 'Voided' && !v.deletedUtc).length;

    const toggleDeleted = (personId: string) => {
        setShowDeleted(prev => ({ ...prev, [personId]: !prev[personId] }));
    };

    return (
        <div className="gv-modal-backdrop" onClick={onClose}>
            <div className="gv-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="gv-modal-header">
                    <div>
                        <h2 className="gv-modal-title">
                            <i className="fa-solid fa-ticket"></i>
                            Program Vouchers
                        </h2>
                        {!isLoading && data && totalVouchers > 0 && (
                            <div className="gv-summary-pills">
                                <span className="gv-pill">{totalVouchers} total</span>
                                {totalActive > 0 && <span className="gv-pill gv-pill-active">{totalActive} active</span>}
                                {totalUsed > 0 && <span className="gv-pill gv-pill-used">{totalUsed} used</span>}
                                {totalVoided > 0 && <span className="gv-pill gv-pill-voided">{totalVoided} voided</span>}
                            </div>
                        )}
                    </div>
                    <div className="gv-modal-header-actions">
                        {!isLoading && data && !isToggling && (
                            vouchersEnabled ? (
                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => setToggleConfirm('disable')}
                                >
                                    <i className="fa-solid fa-power-off me-1"></i>Disable Vouchers
                                </button>
                            ) : (
                                <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => setToggleConfirm('enable')}
                                >
                                    <i className="fa-solid fa-bolt me-1"></i>Enable Vouchers
                                </button>
                            )
                        )}
                        {isToggling && (
                            <button className="btn btn-sm btn-outline-secondary" disabled>
                                <span className="spinner-border spinner-border-sm me-1" role="status" />
                                {vouchersEnabled ? 'Disabling...' : 'Enabling...'}
                            </button>
                        )}
                        {vouchersEnabled && totalActive > 0 && (
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowPrintAllPicker(true)}>
                                <i className="fa-solid fa-print me-1"></i>Print All
                            </button>
                        )}
                        <button className="btn btn-sm btn-outline-primary" onClick={loadVouchers} disabled={isLoading}>
                            <i className="fa-solid fa-rotate me-1"></i>Refresh
                        </button>
                        <button className="gv-modal-close" onClick={onClose}>&times;</button>
                    </div>
                </div>

                {/* Body */}
                <div className="gv-modal-body">
                    {isLoading && (
                        <div className="gv-empty-state">
                            <div className="spinner-border text-muted" role="status" />
                            <p>Loading vouchers...</p>
                        </div>
                    )}

                    {error && (
                        <div className="gv-empty-state">
                            <i className="fa-solid fa-circle-exclamation text-danger" style={{ fontSize: '2rem' }}></i>
                            <p className="text-danger">{error}</p>
                            <button className="btn btn-sm btn-outline-primary" onClick={loadVouchers}>Retry</button>
                        </div>
                    )}

                    {/* Disabled warning banner */}
                    {!isLoading && !error && data && !vouchersEnabled && (
                        <div className="alert alert-warning d-flex align-items-start gap-2 py-2 mb-3" role="alert">
                            <i className="fa-solid fa-triangle-exclamation mt-1"></i>
                            <div>
                                <strong>Automatic voucher generation is disabled</strong>
                                <div className="small">Program vouchers will not be generated or updated when the reservation changes. Ad-hoc vouchers can still be created.</div>
                            </div>
                        </div>
                    )}

                    {/* No vouchers yet, but guests exist - explain eligibility while still
                        listing guests below so ad-hoc vouchers can be added manually. */}
                    {!isLoading && !error && data && data.people.length > 0 && totalVouchers === 0 && (
                        <div className="alert alert-light border d-flex align-items-start gap-2 py-2 mb-3" role="note">
                            <i className="fa-solid fa-circle-info mt-1 text-muted"></i>
                            <div className="small text-muted">
                                No vouchers have been generated for this reservation.
                                {vouchersEnabled
                                    ? ' Vouchers are generated automatically for guests on eligible programs (Total Fitness, Weight Loss) with 7+ program days. You can still add ad-hoc vouchers manually below.'
                                    : ' Enable voucher generation to create program vouchers, or add ad-hoc vouchers manually below.'}
                            </div>
                        </div>
                    )}

                    {/* No guests on the reservation at all */}
                    {!isLoading && !error && data && data.people.length === 0 && (
                        <div className="gv-empty-state">
                            <i className="fa-solid fa-ticket" style={{ fontSize: '2.5rem', color: '#d1d5db' }}></i>
                            <p className="mt-2 mb-1 fw-medium">No guests on this reservation</p>
                            <p className="text-muted small mb-0">
                                Add guests to the reservation to generate or add vouchers.
                            </p>
                        </div>
                    )}

                    {!isLoading && !error && data && data.people.length > 0 && data.people.map(person => {
                        const override = overrides.find(o => o.personId === person.personId);
                        const activeVouchers = person.vouchers.filter(v => !v.deletedUtc);
                        const deletedVouchers = person.vouchers.filter(v => v.deletedUtc);
                        const personRed = activeVouchers.filter(v => v.voucherType === 'Red').length;
                        const personBlue = activeVouchers.filter(v => v.voucherType === 'Blue').length;

                        return (
                            <div key={person.personId} className="gv-person-section">
                                {/* Person header */}
                                <div className="gv-person-header">
                                    <div className="gv-person-info">
                                        <i className="fa-solid fa-user"></i>
                                        <span className="gv-person-name">{person.personName || 'Unknown Guest'}</span>
                                        <span className="gv-person-counts">
                                            <span className="gv-count-red">{personRed}R</span>
                                            <span className="gv-count-blue">{personBlue}B</span>
                                        </span>
                                    </div>
                                    <div className="gv-person-actions">
                                        <OverrideBadge
                                            override={override ?? null}
                                            onClick={() => setOverridePersonId(person.personId)}
                                        />
                                        <button
                                            className="btn btn-sm btn-outline-success"
                                            onClick={() => setAdhocPersonId(person.personId)}
                                            title="Add ad-hoc voucher"
                                        >
                                            <i className="fa-solid fa-plus me-1"></i>Add
                                        </button>
                                    </div>
                                </div>

                                {/* Voucher cards grouped by date */}
                                {groupVouchersByDate(activeVouchers).map(group => (
                                    <div key={group.sortKey} className="gv-date-group">
                                        <div className="gv-date-group-label">{group.label}</div>
                                        <div className="gv-cards-grid">
                                            {group.vouchers.map(voucher => (
                                                <GuestVoucherCard
                                                    key={voucher.guestVoucherId}
                                                    voucher={voucher}
                                                    companyManagerBaseUrl={data.companyManagerBaseUrl}
                                                    onConvert={setConvertingVoucher}
                                                    onPrint={setPrintingVoucher}
                                                    onResolveFlag={handleResolveFlag}
                                                    onRevert={setRevertingVoucher}
                                                    onVoid={handleVoid}
                                                    onUnvoid={handleUnvoid}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {/* Deleted vouchers */}
                                {deletedVouchers.length > 0 && (
                                    <div className="gv-deleted-section">
                                        <button className="gv-deleted-toggle" onClick={() => toggleDeleted(person.personId)}>
                                            <i className={`fa-solid fa-chevron-${showDeleted[person.personId] ? 'down' : 'right'} me-1`}></i>
                                            {deletedVouchers.length} deleted voucher{deletedVouchers.length !== 1 ? 's' : ''}
                                        </button>
                                        {showDeleted[person.personId] && groupVouchersByDate(deletedVouchers).map(group => (
                                            <div key={group.sortKey} className="gv-date-group">
                                                <div className="gv-date-group-label">{group.label}</div>
                                                <div className="gv-cards-grid">
                                                    {group.vouchers.map(voucher => (
                                                        <GuestVoucherCard key={voucher.guestVoucherId} voucher={voucher} companyManagerBaseUrl={data.companyManagerBaseUrl} isDeleted />
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Sub-modals */}
                <VoucherActionModal
                    key={convertingVoucher?.guestVoucherId ?? 'none'}
                    voucher={convertingVoucher}
                    onClose={() => setConvertingVoucher(null)}
                    onConverted={loadVouchers}
                    onReconciled={loadVouchers}
                    companyManagerBaseUrl={data?.companyManagerBaseUrl ?? null}
                    portal={false}
                />
                <RevertModal
                    voucher={revertingVoucher}
                    onClose={() => setRevertingVoucher(null)}
                    onReverted={() => { setRevertingVoucher(null); loadVouchers(); }}
                />
                <PrinterPickerModal
                    show={printingVoucher !== null}
                    title={`Print V-${printingVoucher?.voucherNumber ?? ''}`}
                    onPrint={async (printerId) => { if (printingVoucher) await handleThermalPrint(printingVoucher.guestVoucherId, printerId); setPrintingVoucher(null); }}
                    onPreview={() => { if (printingVoucher) handlePreviewPrint(printingVoucher.guestVoucherId); setPrintingVoucher(null); }}
                    onClose={() => setPrintingVoucher(null)}
                />
                <PrinterPickerModal
                    show={showPrintAllPicker}
                    title={`Print All (${totalActive})`}
                    onPrint={async (printerId) => { await handleThermalPrintAll(printerId); setShowPrintAllPicker(false); }}
                    onPreview={() => { handlePreviewPrintAll(); setShowPrintAllPicker(false); }}
                    onClose={() => setShowPrintAllPicker(false)}
                />
                {adhocPersonId && (() => {
                    const person = data?.people.find(p => p.personId === adhocPersonId);
                    return (
                        <AdHocVoucherModal
                            reservationId={reservationId}
                            personId={adhocPersonId}
                            personName={person?.personName || 'Unknown Guest'}
                            onCreated={() => { setAdhocPersonId(null); loadVouchers(); }}
                            onClose={() => setAdhocPersonId(null)}
                        />
                    );
                })()}
                {overridePersonId && (() => {
                    const person = data?.people.find(p => p.personId === overridePersonId);
                    const override = overrides.find(o => o.personId === overridePersonId);
                    return (
                        <OverrideModal
                            reservationId={reservationId}
                            personId={overridePersonId}
                            personName={person?.personName || 'Unknown Guest'}
                            override={override ?? null}
                            onSaved={() => { loadOverrides(); loadVouchers(); }}
                            onClose={() => setOverridePersonId(null)}
                        />
                    );
                })()}
                <VoucherToggleConfirmModal
                    action={toggleConfirm}
                    onConfirm={handleToggleConfirm}
                    onCancel={() => setToggleConfirm(null)}
                />
            </div>
        </div>
    );
};
